<template>
  <div class="max-w-7xl mx-auto sm:px-6 lg:px-16 py-8">
    <div class="mb-6">
      <button
        @click="$router.go(-1)"
        class="flex items-center text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft class="w-5 h-5 mr-1" />
        <span class="font-medium">Back</span>
      </button>
    </div>

    <!-- Main Content Area -->
    <div class="grid grid-cols-1 xl:grid-cols-3 gap-8 items-start">
      <div class="xl:col-span-2 space-y-8 rounded-2xl">
        <div class="">
          <h2 class="text-xl font-bold text-foreground mb-2">Payment Method</h2>
          <div class="space-y-4">
            <div class="space-y-2">
              <div class="relative">
                <Input
                  type="text"
                  placeholder="Card Number"
                  class="w-full h-14 pr-12 bg-white"
                />
                <div
                  class="flex absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                >
                  <img
                    src="/Mastercard.svg"
                    alt="Mastercard"
                    class="h-6 w-auto"
                  />
                  <img src="/Visa.svg" alt="Visa" class="h-6 w-auto" />
                </div>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div class="space-y-2">
                <Input
                  type="text"
                  placeholder="Expiry Date"
                  class="w-full h-14"
                />
              </div>
              <div class="space-y-2">
                <Input type="password" placeholder="CVV" class="w-full h-14" />
              </div>
            </div>
          </div>
        </div>
        <div class="">
          <h2 class="text-xl font-bold text-foreground mb-2">
            Billing Address
          </h2>
          <div class="space-y-4">
            <div class="space-y-2">
              <div class="relative">
                <Input
                  type="text"
                  placeholder="Full Name"
                  class="w-full h-14 pr-12 bg-white"
                />
              </div>
            </div>
            <div class="space-y-2">
              <Select>
                <SelectTrigger class="w-full h-14 bg-white">
                  <SelectValue placeholder="Country / Region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>North America</SelectLabel>
                    <SelectItem value="us">United States</SelectItem>
                    <SelectItem value="ca">Canada</SelectItem>
                    <SelectItem value="mx">Mexico</SelectItem>
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>Europe</SelectLabel>
                    <SelectItem value="uk">United Kingdom</SelectItem>
                    <SelectItem value="de">Germany</SelectItem>
                    <SelectItem value="fr">France</SelectItem>
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>Africa</SelectLabel>
                    <SelectItem value="ng">Nigeria</SelectItem>
                    <SelectItem value="gh">Ghana</SelectItem>
                    <SelectItem value="ke">Kenya</SelectItem>
                    <SelectItem value="za">South Africa</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div class="space-y-2">
              <Input type="text" placeholder="Address" class="w-full h-14" />
            </div>
          </div>
        </div>
      </div>

      <!-- Plan Summary Column -->
      <div
        v-if="selectedPlan"
        class="bg-card rounded-2xl p-6 lg:p-8 border border-border flex flex-col h-full sticky top-8"
      >
        <div class="flex items-center justify-between mb-8">
          <h3 class="text-foreground font-bold text-2xl mb-2">
            {{ selectedPlan.name }}
          </h3>
          <a
            href="/pricing"
            class="text-muted-foreground text-[15px] leading-relaxed underline"
          >
            Change plans
          </a>
        </div>
        <div class="space-y-4 mb-8 flex-1">
          <div
            v-for="feature in selectedPlan.features"
            :key="feature"
            class="flex items-start gap-3"
          >
            <Check class="w-5 h-5 text-muted-foreground mt-0.5" />
            <p class="text-[14px] text-foreground">{{ feature }}</p>
          </div>
        </div>
        <!-- <div class="mb-8 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div class="flex items-center justify-between mb-2">
            <span class="text-gray-600">Billing Cycle</span>
            <span class="font-medium text-gray-900">{{ activeTab }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-gray-600">Price</span>
            <div class="text-right">
              <span class="text-2xl font-bold text-gray-900">
                ${{ calculateDiscountedPrice(selectedPlan) }}
              </span>
              <span class="text-gray-500"
                >/ {{ activeTab === "Monthly" ? "mo" : "yr" }}</span
              >
            </div>
          </div>
        </div> -->

        <div class="mb-4 border-t border-border pt-4 space-y-2">
          <div class="flex items-center justify-between text-[14px]">
            <span class="text-muted-foreground">Annual subscription</span>
            <div class="text-right">
              <span class="font-bold text-foreground">
                ${{ calculateDiscountedPrice(selectedPlan) }}
              </span>
              <span class="text-muted-foreground">
                / {{ activeTab === "Monthly" ? "mo" : "yr" }}</span
              >
            </div>
          </div>
          <div class="flex items-center justify-between text-[14px]">
            <span class="text-muted-foreground">VAT</span>
            <div class="text-right">
              <span class="font-bold text-foreground">$0.00</span>
            </div>
          </div>
        </div>

        <div
          class="flex items-center justify-between border-t border-border pt-4 mb-4"
        >
          <span class="text-muted-foreground">Total</span>
          <div class="text-right">
            <span class="text-2xl font-bold text-foreground">
              ${{ calculateDiscountedPrice(selectedPlan) }}
            </span>
          </div>
        </div>

        <Button
          class="w-full h-14 text-lg font-semibold hover:bg-primary-700 text-white"
        >
          Pay ${{ calculateDiscountedPrice(selectedPlan) }}
        </Button>
      </div>

      <!-- Empty State / No Plan -->
      <div
        v-else
        class="bg-card rounded-2xl border border-border p-12 text-center"
      >
        <div
          class="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <Search class="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 class="text-xl font-semibold text-foreground mb-2">
          Plan not found
        </h3>
        <p class="text-muted-foreground mb-6">
          Please go back to the pricing page to select a plan.
        </p>
        <router-link
          to="/pricing"
          class="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          View Pricing
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, Search, ChevronLeft } from "lucide-vue-next";

export default {
  name: "CheckoutComponent",
  components: {
    Button,
    Input,
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
    Check,
    Search,
    ChevronLeft,
  },
  data() {
    return {
      showCreateHubModal: false,
      selectedHub: null,
      activeTab: "Monthly",
      tabs: [
        { id: "projects", name: "Projects" },
        { id: "members", name: "Members" },
        { id: "activity", name: "Activity" },
      ],
      pricingPlans: [
        {
          id: 1,
          name: "Free",
          description:
            "Best for anyone who wants to try out all the available tools",
          price: 0.0,
          currency: "USD",
          features: [
            "Create up to 2 learning goals",
            "Access to ES Creative Community",
          ],
          actionText: "Get Free",
          billingCycle: "Monthly",
          billingOptions: [
            {
              id: 1,
              name: "Monthly",
              price: 0.0,
              currency: "USD",
            },
            {
              id: 2,
              name: "Yearly",
              price: 0.0,
              currency: "USD",
            },
          ],
        },
        {
          id: 2,
          name: "Premium",
          description: "Learn and explore new opportunities today!",
          price: 10.0,
          currency: "USD",
          features: [
            "80 monthly Edgespace AI credits",
            "Create up to 100 learning goals",
            "Fast and efficient model response",
            "Access to ES Creative Community",
          ],
          actionText: "Get Premium",
          billingCycle: "Monthly",
          billingOptions: [
            {
              id: 1,
              name: "Monthly",
              price: 10.0,
              currency: "USD",
            },
            {
              id: 2,
              name: "Yearly",
              price: 120.0,
              discount: 30,
              currency: "USD",
            },
          ],
        },
        {
          id: 3,
          name: "Premium Pro",
          description:
            "Best for anyone who wants to try out all the available tools",
          price: 30.0,
          currency: "USD",
          features: [
            "200 monthly Edgespace AI credits",
            "Unlimited learning goals",
            "Fast and efficient model response",
            "Access to ES Creative Community",
          ],
          actionText: "Get Premium Pro",
          billingCycle: "Monthly",
          billingOptions: [
            {
              id: 1,
              name: "Monthly",
              price: 30.0,
              currency: "USD",
            },
            {
              id: 2,
              name: "Yearly",
              price: 360.0,
              discount: 30,
              currency: "USD",
            },
          ],
        },
      ],
    };
  },
  computed: {
    selectedPlan() {
      const planId = parseInt(this.$route.query.planId);
      return this.pricingPlans.find((p) => p.id === planId) || null;
    },
  },
  watch: {
    "$route.query.billing": {
      immediate: true,
      handler(newBilling) {
        if (
          newBilling &&
          (newBilling === "Monthly" || newBilling === "Yearly")
        ) {
          this.activeTab = newBilling;
        }
      },
    },
  },
  methods: {
    selectHub(hub) {
      this.selectedHub = hub;
      this.activeTab = "projects";
    },
    formatDate(date) {
      return dayjs(date).fromNow();
    },
    getStatusClass(status) {
      const classes = {
        "In Progress": "bg-blue-100 text-blue-800",
        Planning: "bg-yellow-100 text-yellow-800",
        Completed: "bg-green-100 text-green-800",
      };
      return classes[status] || "bg-gray-100 text-gray-800";
    },
    currentPrice(plan) {
      if (!plan.billingOptions) return plan.price;
      const option = plan.billingOptions.find((o) => o.name === this.activeTab);
      return option ? option.price : plan.price;
    },
    calculateDiscountedPrice(plan) {
      if (!plan.billingOptions) return plan.price;
      const option = plan.billingOptions.find((o) => o.name === this.activeTab);
      if (option && option.discount) {
        return option.price - (option.price * option.discount) / 100;
      }
      return option ? option.price : plan.price;
    },
  },
  mounted() {
    window.scrollTo(0, 0);
  },
};
</script>

<style scoped>
/* Additional styles if needed */
</style>
